import { useState } from 'react';
import {
  requestRephrase,
  saveNarrative,
  type RephrasableField,
  type RephraseResult,
} from '../../lib/lesson-api.js';
import { ApiError } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * One narrative field, with its AI rewrite.
 *
 * ADR-0008 in the interface. Three properties are non-negotiable, and each one is
 * a thing this component deliberately does NOT do:
 *
 *  1. **It never replaces the field silently.** The suggestion appears BESIDE the
 *     author's own words, not in place of them. Until they choose, what is stored
 *     is what they wrote.
 *  2. **There is no way past it without a disposition.** Accept, Edit and Reject
 *     are the only three exits, and the API records which one — verified against
 *     the suggestion's own text, so a client claiming "accepted" while sending
 *     something else is recorded as an edit.
 *  3. **Unavailable is a normal state, not an error.** No provider configured, a
 *     timeout, a provider that failed: the field stays editable and the work
 *     continues. An AI outage must never stop someone recording what they saw.
 */

const FIELD_LABELS: Record<RephrasableField, string> = {
  description: 'Description of the issue',
  rootCause: 'Root cause',
  correction: 'Correction taken',
  correctiveAction: 'Corrective action',
};

const FIELD_HINTS: Record<RephrasableField, string> = {
  description: 'What happened, in your own words.',
  rootCause: 'Why it happened — the underlying cause, not the symptom.',
  correction: 'What was done to fix this instance.',
  correctiveAction: 'What will stop it happening again.',
};

export interface RephrasePanelProps {
  lessonId: string;
  field: RephrasableField;
  value: string;
  onChange: (value: string) => void;
  /** Saving is disabled once the record leaves the author's hands. */
  readOnly?: boolean;
  /** Offline: the field still works, the rewrite cannot be asked for. */
  online?: boolean;
  onSaved?: (value: string) => void;
}

export function RephrasePanel({
  lessonId,
  field,
  value,
  onChange,
  readOnly = false,
  online = true,
  onSaved,
}: RephrasePanelProps) {
  const [suggestion, setSuggestion] = useState<RephraseResult | null>(null);
  const [editing, setEditing] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const label = FIELD_LABELS[field];
  const textareaId = `field-${field}`;

  const ask = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await requestRephrase(lessonId, field, value);
      if (result.text) {
        setSuggestion(result);
        setEditing(result.text);
      } else {
        // Switched off, no provider, or a timeout. All ordinary.
        setNotice(result.unavailableReason ?? t("No rewrite is available right now."));
      }
    } catch (caught) {
      setNotice(
        caught instanceof ApiError ? caught.message : t("The rewrite could not be requested."),
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Save with a disposition.
   *
   * The disposition is derived from what is actually being saved rather than from
   * which button was pressed — `accepted` only when the text is the suggestion
   * verbatim. The API checks this independently; agreeing with it here just keeps
   * the two from disagreeing for no reason.
   */
  const dispose = async (intent: 'accepted' | 'edited' | 'rejected') => {
    if (!suggestion) return;
    setBusy(true);
    setNotice(null);

    const finalText = intent === 'rejected' ? value : editing;
    const disposition =
      intent === 'rejected'
        ? 'rejected'
        : finalText.trim() === (suggestion.text ?? '').trim()
          ? 'accepted'
          : 'edited';

    try {
      await saveNarrative(lessonId, {
        field,
        value: finalText,
        aiSuggestionId: suggestion.suggestionId,
        aiDisposition: disposition,
      });
      onChange(finalText);
      onSaved?.(finalText);
      setSuggestion(null);
      setNotice(
        disposition === 'rejected'
          ? t("Rewrite rejected. Your own wording is kept.")
          : disposition === 'edited'
            ? t("Saved as your edit of the rewrite.")
            : t("Rewrite accepted and saved."),
      );
    } catch (caught) {
      setNotice(caught instanceof ApiError ? caught.message : t("That could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-md border border-border bg-surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label htmlFor={textareaId} className="text-sm font-semibold">
          {label}
        </label>
        <button
          type="button"
          onClick={() => void ask()}
          disabled={busy || readOnly || !online || !value.trim()}
          title={
            !online
              ? t("A rewrite needs a connection. Your own wording works offline.")
              : !value.trim()
                ? t("Write something first.")
                : undefined
          }
          className="rounded-sm border border-border-strong px-2.5 py-1 text-xs font-medium disabled:opacity-50"
        >
          {busy ? t("Working…") : t("Rephrase with AI")}
        </button>
      </div>

      <p className="mt-1 text-xs text-content-secondary">{FIELD_HINTS[field]}</p>

      <textarea
        id={textareaId}
        value={value}
        readOnly={readOnly}
        onChange={(event) => onChange(event.target.value)}
        rows={4}
        className="mt-2 w-full rounded-sm border border-border-strong px-3 py-2 text-sm read-only:bg-surface-sunken"
      />

      {notice && (
        <p role="status" className="mt-2 text-xs text-content-secondary">
          {notice}
        </p>
      )}

      {suggestion?.text && (
        <div
          className="mt-3 rounded-sm border border-border p-3"
          style={{
            background: 'var(--tone-pending-surface)',
            borderInlineStartWidth: '3px',
            borderInlineStartColor: 'var(--ag-turquoise)',
          }}
        >
          <p className="m-0 text-xs font-semibold uppercase tracking-wide text-content-secondary">
            {t("Suggested rewrite — yours until you choose")}
          </p>

          <textarea
            aria-label={t("Suggested rewrite of {0}", { 0: label })}
            value={editing}
            onChange={(event) => setEditing(event.target.value)}
            rows={4}
            className="mt-2 w-full rounded-sm border border-border-strong bg-surface px-3 py-2 text-sm"
          />

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void dispose('accepted')}
              disabled={busy}
              className="rounded-sm bg-ag-purple px-3 py-1.5 text-sm font-medium text-content-inverse disabled:opacity-60"
            >
              {t("Accept")}
            </button>
            <button
              type="button"
              onClick={() => void dispose('edited')}
              disabled={busy}
              className="rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {t("Save my edit")}
            </button>
            <button
              type="button"
              onClick={() => void dispose('rejected')}
              disabled={busy}
              className="rounded-sm border border-border-strong px-3 py-1.5 text-sm font-medium disabled:opacity-60"
            >
              {t("Reject")}
            </button>
          </div>

          <p className="mt-2 text-xs text-content-secondary">
            {t("Whichever you choose is recorded against this record.")}
          </p>
        </div>
      )}
    </section>
  );
}
